import React, { useContext, useEffect, useState, useRef } from "react";
import { Col, Row } from "react-bootstrap";
import { UserContext } from "shared/context/UserContext";
import SharedMainSelectableClientGrid from "shared/components/grids/clientGrid/SharedMainSelectableClientGrid";
import { GridColumn } from "@progress/kendo-react-grid";
import {
    DefaultNoFilterColumnWidth,
    DefaultColumnWidth,
    DefaultWideColumnWidth,
} from "../../../components/grid/Kendo/KendoGridAndColumnConstants";
import { GetDataWithCleanedDates } from "shared/helpers/DateHelpers.js";
import Accordion from "../../../components/Accordion";
import TparActiveForm from "./TparActiveForm";
import { ParentCompanyDisplayElement } from "shared/components/parentCompanySelectionForGroupUsers";
import { useMutateGenerateNewRequest } from "./hooks/useMutateGenerateNewRequest";
import { useMutateUnsuppressParentCompany } from "./hooks/useMutateUnsuppressParentCompany";
import { useMutateDeleteReports } from "./hooks/useMutateDeleteReports";
import { useFetchTparHistoryMaxDate } from "./hooks/useFetchTparHistoryMaxDate";
import { ControlSpinner } from "shared/components/spinners/ControlSpinner";
import { OrganizationTypes } from "shared/constants/OrganizationTypes";
import { useReactQueryHelpers } from "shared/hooks/useReactQueryHelpers";
import axios from "axios";
import {
    Base64ToArrayBuffer,
    SaveByteArray,
} from "shared/helpers/Base64ArrayTranslator";

export function TparActive(props) {
    const { passedProps } = props;
    const gridRef = useRef(null);
    const userConfig = useContext(UserContext);
    const { invalidateReactQueryKey } = useReactQueryHelpers();

    // Get parent company attributes from props (passed down from Tpar.js)
    const {
        parentCompanyAttributes,
        isLoadingAttributes,
        attributesError,
        refetchAttributes,
        currentParentCompanyId
    } = passedProps;

    const [tparGridApiParams, setTparGridApiParams] = useState(null);
    const [downloadingRowKey, setDownloadingRowKey] = useState(null);
    const [activeRequestsGridData, setActiveRequestsGridData] = useState(null);
    const [refreshTrigger, setRefreshTrigger] = useState(0);

    // Initialize the mutation hooks
    const generateNewRequestMutation = useMutateGenerateNewRequest();
    const unsuppressParentCompanyMutation = useMutateUnsuppressParentCompany();
    const deleteReportsMutation = useMutateDeleteReports();

    // NOTE (NETX-5821): never use currentOrganization.value as the RPID for a Group user —
    // that value is the GROUP id and is rejected (403) by RPID-scoped endpoints. Only a
    // ParentCompany org (or an explicit parent-company selection) yields a valid RPID.
    let selectedParentCompanyId =
        passedProps.parentCompanySetByGroupUser &&
            passedProps.parentCompanySetByGroupUser.rpid
            ? passedProps.parentCompanySetByGroupUser.rpid
            : userConfig &&
              userConfig.currentOrganization &&
              userConfig.currentOrganization.type === OrganizationTypes.ParentCompany
            ? userConfig.currentOrganization.value
            : null;

    // Fetch the maximum end date from history to calculate the default start date
    const { data: calculatedStartDate, isLoading: isLoadingHistoryMaxDate } = useFetchTparHistoryMaxDate(
        selectedParentCompanyId,
        userConfig && userConfig.currentOrganization && userConfig.currentOrganization.value
    );

    useEffect(() => {
        if (userConfig && userConfig.currentOrganization && userConfig.currentOrganization.value) {

            if (selectedParentCompanyId) {
                setTparGridApiParams({
                    parentCompanyId: selectedParentCompanyId,
                    refreshTrigger: refreshTrigger
                });
            }
        }
    }, [userConfig, selectedParentCompanyId, passedProps.parentCompanySetByGroupUser, refreshTrigger]);

    function onGridSourceDataChanged(newData) {
        if (newData && Array.isArray(newData)) {
            setActiveRequestsGridData(newData);
        } else {
            setActiveRequestsGridData([]);
        }
    }

    // Data processor to handle the response data
    function RequestsDataProcessor(responseData) {
        let dataWithCleanedDates = GetDataWithCleanedDates(responseData);
        return dataWithCleanedDates;
    }

    function handleGenerateReports(formData) {
        formData.rpid = selectedParentCompanyId;

        const requestData = {
            rpid: formData.rpid,
            userName: userConfig.email,
            startDate: formData.startDate,
            endDate: formData.endDate
        };

        generateNewRequestMutation.mutate(requestData, {
            onSuccess: (data) => {
                console.log("Generate Reports successful:", data);
                // Invalidate the grid's React Query cache to trigger refetch
                invalidateReactQueryKey(["useFetchClientGridDataDataKey", "api/ThirdPartyReceivable/Get", tparGridApiParams]);
                // NOTE TODO: Optionally show success message
            },
            onError: (error) => {
                console.error("Generate Reports failed:", error);
                // Handle error - show error message to user
                alert("Failed to generate reports. Please try again.");
            },
        });
    }

    function handleUnsuppressAccount() {
        let confirmation = window.confirm("IMPORTANT: Selecting OK will remove account suppression and initiate the upload of your claim and payment data to the website. Click Cancel to keep suppression enabled.");

        if (confirmation === true) {
            // Call the ThirdPartyReceivable.UnsuppressParentCompany endpoint
            unsuppressParentCompanyMutation.mutate({
                rpid: currentParentCompanyId,
                email: userConfig.email
            }, {
                onSuccess: (data) => {
                    alert("Account has been successfully unsuppressed.");
                    console.log("Unsuppress account success:", data);

                    // Invalidate the grid's React Query cache to trigger refetch
                    invalidateReactQueryKey(["useFetchClientGridDataDataKey", "api/ThirdPartyReceivable/Get", tparGridApiParams]);

                    // Invalidate the parent company attributes cache in Layout.js to force re-render of ScreenHeader
                    invalidateReactQueryKey(["FetchParentCompanyAttributes", currentParentCompanyId]);
                },
                onError: (error) => {
                    console.error("Error unsuppressing account:", error);
                    alert("Failed to unsuppress account. Please try again.");
                }
            });
        }
        else {
            alert("You have canceled the unsuppress account action.");
        }
    }

    function handleDeleteReports() {
        let confirmation = window.confirm("You're about to permanently delete these reports. This action cannot be undone. Do you wish to continue?");

        if (confirmation === true) {
            // Call the delete reports mutation
            deleteReportsMutation.mutate({
                rpid: selectedParentCompanyId,
                userId: userConfig.email
            }, {
                onSuccess: (data) => {
                    console.log("Delete Reports successful:", data);
                    alert("Reports have been successfully deleted.");

                    // Invalidate the grid's React Query cache to trigger refetch
                    invalidateReactQueryKey(["useFetchClientGridDataDataKey", "api/ThirdPartyReceivable/Get", tparGridApiParams]);

                    // Force grid refresh by updating the refresh trigger
                    setRefreshTrigger(prev => prev + 1);
                },
                onError: (error) => {
                    console.error("Delete Reports failed:", error);

                    // Check if it's a 404 (no reports found)
                    if (error.response && error.response.status === 404) {
                        alert("No reports found to delete for the current month.");
                    } else {
                        alert("Failed to delete reports. Please try again or contact support.");
                    }
                }
            });
        }
    }

    async function handleDownloadReport(fileName, rpid) {
        try {
            setDownloadingRowKey(`${rpid}_${fileName}`);

            const accessToken = await passedProps.auth.getAccessToken();

            const response = await axios.get(`api/ThirdPartyReceivable/DownloadFromFileShare`, {
                params: {
                    fileNameInput: fileName,
                    rpidInput: rpid
                },
                headers: { Authorization: `Bearer ${accessToken}` },
            });

            if (response.data) {
                var dataAsArray = Base64ToArrayBuffer(response.data);
                SaveByteArray(fileName, dataAsArray);
            } else {
                alert("Unable to download the report, please contact Net-Rx support.");
            }
        } catch (error) {
            console.error("Download failed:", error);
            alert("Failed to download the report. Please try again.");
        } finally {
            setDownloadingRowKey(null);
        }
    }

    //let DownloadDataCell = (props) => {
    //    let dataRow = props.dataItem;
    //    const rowKey = `${dataRow.rpid}_${dataRow.reportName}-en-${dataRow.rpid}_${dataRow.id}.xlsx`;
    //    const isDownloading = downloadingRowKey === rowKey;

    //    //NOTE: This is the fileName construction logic
    //    if (dataRow.reportStatus === "Completed") {
    //        return (
    //            <td
    //                {...props.tdProps}
    //                colSpan={1}
    //                style={{
    //                    color: "#015CAB",
    //                }}
    //            >
    //                <a
    //                    style={{
    //                        cursor: isDownloading ? "wait" : "pointer",
    //                        opacity: isDownloading ? 0.6 : 1,
    //                    }}
    //                    onClick={
    //                        isDownloading ? undefined : () => {
    //                            console.log(dataRow);
    //                            let fileName = dataRow.reportName + "-en-" + dataRow.rpid + "_" + dataRow.id + ".xlsx";
    //                            console.log(fileName);

    //                            // Call the download function
    //                            //handleDownloadReport(fileName, dataRow.rpid);
    //                        }
    //                    }
    //                >
    //                    {isDownloading ? "Downloading..." : "Download"}
    //                </a>
    //            </td>
    //        );
    //    } else {
    //        return (
    //            <td {...props.tdProps} colSpan={1}>
    //            </td>
    //        );
    //    }
    //};


    let DownloadDataCell = (props) => {
        let dataRow = props.dataItem;

        //NOTE: This is the fileName construction logic

        //NOTE: Restore this later when download functionality is implemented
        if (dataRow.reportStatus === "Completed") {
            return (
                <td
                    {...props.tdProps}
                    colSpan={1}
                    style={{
                        color: "#015CAB",
                    }}
                >
                    <a
                        style={{
                            cursor: "pointer",
                        }}
                        onClick={
                            () => {
                                let fileName = dataRow.reportName + "-en-" + dataRow.rpid + "_" + dataRow.id + ".xlsx";
                                //alert("You have clicked the download button.");

                                //NOTE TODO: using the fileName and dataRow.rpid value, download the file
                                handleDownloadReport(fileName, dataRow.rpid);
                            }
                        }
                    >
                        Download
                    </a>
                </td>
            );
        } else {
            return (
                <td {...props.tdProps} colSpan={1}>
                </td>
            );
        }
    };


    let gridUseQueryOptions = { refetchOnWindowFocus: false };

    // Show spinner when any mutation is pending
    const isLoading = generateNewRequestMutation.isPending || unsuppressParentCompanyMutation.isPending || deleteReportsMutation.isPending;

    if (isLoading) {
        return <ControlSpinner />;
    }

    return (
        <div className="feature">
            {passedProps.parentCompanySetByGroupUser &&
                passedProps.parentCompanySetByGroupUser.parentCompanyName
                ? ParentCompanyDisplayElement(
                    passedProps.parentCompanySetByGroupUser.parentCompanyName,
                    () => passedProps.resetValuesSelectedByGroupUserCallback()
                )
                : null}

            <Accordion defaultExpanded label="Search and Filter">
                <Row>
                    <Col>
                        <TparActiveForm
                            handleGenerateReports={handleGenerateReports}
                            handleUnsuppressAccount={handleUnsuppressAccount}
                            handleDeleteReports={handleDeleteReports}
                            inputStartDate={calculatedStartDate ? new Date(calculatedStartDate) : null}
                            inputEndDate={new Date()}
                            gridData={activeRequestsGridData}
                            isParentSuppressed={parentCompanyAttributes && parentCompanyAttributes.find(attr => attr.abid === 94) ? true : false}
                        />
                    </Col>
                </Row>
            </Accordion>

            <Row>
                <Col>
                    <div style={{ textAlign: 'center', color: 'black' }}>
                        <strong>
                            Please ensure all remittances for the reporting period have been archived 24 hours prior to generating reports to allow As of Aging to update.
                        </strong>
                    </div>
                </Col>
            </Row>

            <Row>
                <Col className="tight-grid multi-line-filter">
                    <SharedMainSelectableClientGrid
                        ref={gridRef}
                        apiGetEndpoint={"api/ThirdPartyReceivable/Get"}
                        disableCache={true}
                        onDataRetrievedProcessor={RequestsDataProcessor}
                        onGridSourceDataChangedCallback={onGridSourceDataChanged}
                        useQueryOptions={gridUseQueryOptions}
                        apiParameters={tparGridApiParams}
                        dataItemKey={"kendoGridId"}
                        exportFileNamePrefix={"ThirdPartyReceivable"}
                        pageSize={100}
                    >
                        <GridColumn
                            key="downloadColumn"
                            field=""
                            title={"Download"}
                            filterable={false}
                            cells={{
                                data: DownloadDataCell,
                            }}
                            width={DefaultNoFilterColumnWidth()}
                        />
                        <GridColumn
                            key="reportStatusColumn"
                            field="reportStatus"
                            title="Status"
                            width={DefaultColumnWidth()}
                            filterable={true}
                            sortable={true}
                        />
                        <GridColumn
                            key="createdDateTimeColumn"
                            field="createdDateTime"
                            title="Creation Time"
                            filter="date"
                            format="{0:MM/dd/yyyy}"
                            width={DefaultWideColumnWidth()}
                            filterable={true}
                            sortable={true}
                        />
                        <GridColumn
                            key="startDateColumn"
                            field="startDate"
                            title="Report Start Date"
                            filter="date"
                            format="{0:MM/dd/yyyy}"
                            width={DefaultColumnWidth()}
                            filterable={true}
                            sortable={true}
                        />
                        <GridColumn
                            key="endDateColumn"
                            field="endDate"
                            title="Report End Date"
                            filter="date"
                            format="{0:MM/dd/yyyy}"
                            width={DefaultColumnWidth()}
                            filterable={true}
                            sortable={true}
                        />
                        <GridColumn
                            key="reportNameColumn"
                            field="reportName"
                            title="Report Name"
                            width={DefaultWideColumnWidth()}
                            filterable={true}
                            sortable={true}
                        />
                        <GridColumn
                            key="submittedByColumn"
                            field="submittedBy"
                            title="Submitted By"
                            width={DefaultColumnWidth()}
                            filterable={true}
                            sortable={true}
                        />
                    </SharedMainSelectableClientGrid>
                </Col>
            </Row>
        </div>
    );
}